import React from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { buildAdminAttributeRows } from '../utils/announcementAdminPresentation';

type Props = {
  categoryId: number | null | undefined;
  objectAttributes: Record<string, unknown> | null | undefined;
};

export default function AnnouncementAdminAttributes({ categoryId, objectAttributes }: Props) {
  const { data: categoryResponse, isLoading } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => apiService.getCategory(categoryId!),
    enabled: typeof categoryId === 'number' && categoryId > 0,
  });

  const rows = buildAdminAttributeRows(objectAttributes, categoryResponse?.data?.category);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return <Alert severity="info">Aucun attribut renseigné pour cette annonce.</Alert>;
  }

  return (
    <Box>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 42%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {row.label}
            </Typography>
            {row.secret ? <Chip size="small" label="Secret" color="warning" variant="outlined" /> : null}
          </Box>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              textAlign: 'right',
              whiteSpace: 'pre-wrap',
              fontWeight: row.secret ? 700 : 500,
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
